import React, { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import StorySelectModal from '../components/StorySelectModal';
import { IStory } from '../services/types';

interface IProps {
  children: JSX.Element;
}

interface IStorySelectConfig {
  title?: string;
  onSelect: (story: IStory) => void;
  onClose?: () => void;
  filter?: 'story' | 'season';
  scopeRef?: React.RefObject<any>;
}

interface IStorySelectContextValue {
  setConfig: Dispatch<SetStateAction<IStorySelectConfig>>;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

interface IUseStorySelect {
  show: (config: IStorySelectConfig) => void;
  hide: () => void;
}

const initialState: IStorySelectConfig = {
  title: 'Select a story',
  onSelect: () => {},
  onClose: () => {},
};

export const StorySelectContextComponent = (props: IProps): JSX.Element => {
  const [config, setConfig] = useState<IStorySelectConfig>(initialState);
  const { title, onSelect, onClose, filter, scopeRef } = config;

  const [open, setOpen] = useState<boolean>(false);

  const handleClick = (story: IStory) => {
    setOpen(false);
    onSelect && onSelect(story);
  };

  const handleClose = () => {
    setOpen(false);
    onClose && onClose();
  };

  useEffect(() => {
    if (!scopeRef) {
      return;
    }

    const io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          setOpen(false);
        }
      });
    });

    io.observe(scopeRef.current);
  }, [scopeRef]);

  return (
    <StorySelectContext.Provider value={{ setOpen, setConfig }}>
      {props.children}
      <StorySelectModal title={title} open={open} onClick={handleClick} onClose={handleClose} filter={filter} />
    </StorySelectContext.Provider>
  );
};

const defaultValue: IStorySelectContextValue = {
  setOpen: () => {},
  setConfig: () => {},
};

const StorySelectContext = createContext<IStorySelectContextValue>(defaultValue);

const useStorySelect = (): IUseStorySelect => {
  const { setOpen, setConfig } = useContext(StorySelectContext);

  const show = (config: IStorySelectConfig) => {
    setConfig(config);
    setOpen(true);
  };

  const hide = () => {
    setOpen(false);
  };

  return { show, hide };
};

export default useStorySelect;
